import * as React from 'react';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import ProficiencyLevelsInfoGraph from 'bundles/enterprise-admin-skills/components/ProficiencyLevelsInfoGraph';

import _t from 'i18n!nls/enterprise-admin-skills';

type FAQ = {
  id: string;
  title: string;
  paragraphs: (string | JSX.Element)[];
  infoGraph?: JSX.Element;
};

export default (): FAQ[] => [
  {
    id: 'howSkillSetsWork',
    title: _t('How do SkillSets work?'),
    paragraphs: [
      _t(
        'Admins in your organization can define SkillSets based on business goals and link those individual SkillSets to Learning Programs. This helps learners within your organization guide their learning journey.'
      ),
      _t('Learners can find SkillSets that are compatible with their current role and future career goals.'),
      _t(
        'They can view their own skill proficiency scores and work towards the target proficiency score shown in the SkillSet.'
      ),
      _t(
        'Course recommendations show learners which skills are covered in different courses. This way they can develop a learning plan specific to skills they want to develop.'
      ),
    ],
  },
  {
    id: 'howLearnersDevelopSkills',
    title: _t('How do learners develop skills?'),
    paragraphs: [
      _t(
        'Upon joining Coursera, a learner’s initial proficiency score for a given skill is zero. That proficiency score increases when the learner passes graded assessments tagged with that skill.'
      ),
      _t(
        'As learners pass more difficult assessments, their skill score will increase faster. As they become more proficient in a skill, they will need to pass more advanced content to increase their score.'
      ),
    ],
  },
  {
    id: 'howAreScoresCalculated',
    title: _t('How are skill scores calculated?'),
    paragraphs: [
      _t(
        'Skill proficiency scores are calculated based on learner performance on graded assessments. These assessments include quizzes, programming assignments, and assignments with links that take learners to third-party websites. Peer-reviewed assignments are not included.'
      ),
      _t(
        'As learners pass assessments, their skill score will increase. The speed of the increase depends on the material’s difficulty rating, which is determined by the pass-fail rate of all the learners taking those assessments. For example, an assessment that most learners with low skill scores pass will have a low difficulty rating compared to an assessment that most learners with high skills scores fail. Thus, the biggest increases will come from passing the most difficult assessments.'
      ),
      _t(
        'Score increases are triggered by passing assessments. The specific grade does not affect skill scores. For example, if the passing grade for an assessment is 80%, the skill score will increase by the same amount when the learner achieves any grade between 80%-100%.'
      ),
      <FormattedHTMLMessage
        message={_t(
          'If you want to know more about the data model that powers skill scoring, <a href="https://files.eric.ed.gov/fulltext/ED599237.pdf" target="_blank" rel="noopener noreferrer">read this research paper</a>.'
        )}
      />,
    ],
  },
  {
    id: 'whatAreProficiencyLevels',
    title: _t('What are proficiency levels?'),
    paragraphs: [
      _t(
        'Learners’ exact skill scores are not shown to Administrators in your organization. Instead Administrators can see a learner’s proficiency level and the average proficiency of all learners in the organization learning a skill.'
      ),
      _t(
        'A Conversant level indicates that a learner can speak to the importance of a skill and/or its applicability.'
      ),
      _t(
        'Beginner means a learner is on their way to developing application of that skill (beyond just talking about its importance or how it might be used).'
      ),
      _t(
        'Intermediate level indicates the ability to apply basic concepts on the job. A learner can typically reach the intermediate level by passing the assessments in one or two courses.'
      ),
      _t(
        'Advanced level indicates mastery of the material and ability to both teach others and identify novel applications of skills. A learner would typically move from intermediate to advanced after passing assessments across several intermediate courses.'
      ),
    ],
    infoGraph: <ProficiencyLevelsInfoGraph />,
  },
  {
    id: 'whatAreProficiencyTargets',
    title: _t('What are proficiency targets?'),
    paragraphs: [
      _t(
        'Proficiency targets are numerical scores defined by Admins in your organization to communicate skill development goals and expectations to learners.'
      ),
      _t(
        'Each skill in a SkillSet has a proficiency target that learners must achieve. Once they achieve the proficiency target for all skills in the SkillSet, they are considered as having achieved that SkillSet.'
      ),
      _t(
        'Proficiency targets are originally set using Coursera platform data and subject matter experts, but can be customized by Admins in your organization.'
      ),
    ],
  },
  {
    id: 'whenIsLearnerInterested',
    title: _t('When is a learner considered interested in a SkillSet?'),
    paragraphs: [
      _t(
        'A learner is considered interested in a SkillSet for reporting purposes when they enroll in recommended courses associated with the SkillSet.'
      ),
      _t(
        'At least one course enrollment must be initiated from the SkillSet page for the learner to be considered interested.'
      ),
      _t(
        'A learner can be interested in multiple SkillSets at a time if they enroll in multiple courses and the enrollments are initiated from multiple SkillSet pages.'
      ),
    ],
  },
  {
    id: 'whenIsLearnerProgress',
    title: _t('When is a learner considered to be making progress towards a skill?'),
    paragraphs: [
      _t(
        'A learner is considered to be making progress when they have passed at least one assessment tagged to a skill in a SkillSet.'
      ),
      _t(
        'Learners are considered inactive towards a SkillSet when they don’t pass assessments tagged to skills in that SkillSet for an extended period of time.'
      ),
    ],
  },
  {
    id: 'canICustomize',
    title: _t('Can I customize the skills and proficiency targets in a SkillSet?'),
    paragraphs: [
      _t(
        'The ability to customize SkillSets is not available to Admins at this time. Please contact your Customer Success Manager to customize SkillSets.'
      ),
    ],
  },
  {
    id: 'howCanIAddOrRemove',
    title: _t('How can I add or remove a course recommendation?'),
    paragraphs: [
      _t(
        'The ability to customize course recommendations is not available to Admins at this time. Please contact your Customer Success Manager to customize the course recommendations for this SkillSet.'
      ),
    ],
  },
  {
    id: 'whyAutomaticallyUpdated',
    title: _t('Why are some skill-based recommendations automatically updated?'),
    paragraphs: [
      _t(
        'By default, skill-based recommendations are automatically updated when new relevant courses are released by Coursera. Please contact your Customer Success Manager with any questions about specific content updates.'
      ),
    ],
  },
  {
    id: 'whyDuplicateRecommendations',
    title: _t('Why are there duplicate course recommendations across different skills?'),
    paragraphs: [
      _t(
        'A single course can include content that will help learners develop multiple skills. For that reason it is common to see the same course recommended for more than one skill.'
      ),
    ],
  },
  {
    id: 'canIChangeOrderSkillSets',
    title: _t('Can I change the order in which SkillSets are displayed to learners?'),
    paragraphs: [
      _t('Yes. Admins can change the order in which SkillSets are displayed to learners of a given Learning Program.'),
      _t(
        'As an Admin you can visit a Learning Program’s Admin view and drag the SkillSets to the desired order within the catalog tab.'
      ),
    ],
  },
  {
    id: 'canIChangeOrderSkills',
    title: _t('Can I change the order in which skills are shown within a SkillSet?'),
    paragraphs: [
      _t(
        'Yes. You can change the order in which the skills are shown by dragging them to the desired order in the Course Recommendations tab.'
      ),
      _t('By default the skills are ordered based on how relevant they are to the SkillSet.'),
    ],
  },
  {
    id: 'canIChangeOrderCourses',
    title: _t('Can I change the order in which courses are displayed within skill-based recommendations?'),
    paragraphs: [
      _t(
        'No. The order in which courses are shown to learners depends on the learners proficiency level for a given skill. Different learners will see course recommendations ordered differently. Learners with a lower score will see courses with a lower level of difficulty first, while learners with a higher proficiency score will see more difficult courses first.'
      ),
    ],
  },
  {
    id: 'howSeeSkillScore',
    title: _t('How can I see the skill score of a specific learner?'),
    paragraphs: [
      _t(
        'A learner’s detailed skill tracking is private and Administrators will not be able to see a learner’s specific skill proficiency score.'
      ),
      _t(
        'Learner proficiency scores are associated with proficiency levels (conversant, beginner, intermediate, and advanced).'
      ),
      _t(
        'Organization-level and program-level administrators can see a learner’s proficiency level and the average proficiency of all learners in the organization learning a skill.'
      ),
    ],
  },
];
